import { useState } from 'react';
import { CheckCircle2, Loader2, AlertCircle, Info, ChevronRight, ChevronDown, X } from 'lucide-react';
import { ProgressUpdate } from '../../hooks/useProgressStream';

interface ProgressDisplayProps {
  updates: ProgressUpdate[];
  currentProgress: number;
}

const stepLabels: Record<ProgressUpdate['step'], string> = {
  context_gathering: 'Gathering Project Context',
  prompt_building: 'Building AI Prompt',
  prompt_optimization: 'Optimizing Prompt',
  ai_generation: 'Generating Schema with AI',
  validation: 'Validating Schema Quality',
  persistence: 'Saving to Neo4j',
};

const stepOrder: ProgressUpdate['step'][] = [
  'context_gathering',
  'prompt_building',
  'prompt_optimization',
  'ai_generation',
  'validation',
  'persistence',
];

function getStepIcon(step: ProgressUpdate['step'], updates: ProgressUpdate[]) {
  const stepUpdates = updates.filter((u) => u.step === step);
  if (stepUpdates.length === 0) {
    return <div className="w-5 h-5 rounded-full border-2 border-neutral-300" />;
  }

  const latestUpdate = stepUpdates[stepUpdates.length - 1];

  if (latestUpdate.status === 'completed') {
    return <CheckCircle2 className="w-5 h-5 text-green-600" />;
  }

  if (latestUpdate.status === 'failed' || latestUpdate.status === 'error') {
    return <AlertCircle className="w-5 h-5 text-red-600" />;
  }

  if (latestUpdate.status === 'started' || latestUpdate.status === 'in_progress') {
    return <Loader2 className="w-5 h-5 text-primary-600 animate-spin" />;
  }

  return <div className="w-5 h-5 rounded-full border-2 border-neutral-300" />;
}

function getStepStatus(step: ProgressUpdate['step'], updates: ProgressUpdate[]): 'pending' | 'active' | 'completed' | 'failed' {
  const stepUpdates = updates.filter((u) => u.step === step);
  if (stepUpdates.length === 0) return 'pending';

  const latestUpdate = stepUpdates[stepUpdates.length - 1];

  if (latestUpdate.status === 'completed') return 'completed';
  if (latestUpdate.status === 'failed' || latestUpdate.status === 'error') return 'failed';
  if (latestUpdate.status === 'started' || latestUpdate.status === 'in_progress') return 'active';

  return 'pending';
}

function StepItem({ step, updates }: { step: ProgressUpdate['step']; updates: ProgressUpdate[] }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const status = getStepStatus(step, updates);
  const stepUpdates = updates.filter((u) => u.step === step);
  const detailUpdates = stepUpdates.filter((u) => u.status === 'info');
  const latestUpdate = stepUpdates[stepUpdates.length - 1];
  const hasDetails = latestUpdate?.details && Object.keys(latestUpdate.details).length > 0;

  const textColor =
    status === 'completed' ? 'text-green-700' :
    status === 'failed' ? 'text-red-700' :
    status === 'active' ? 'text-primary-700 font-medium' :
    'text-neutral-500';

  return (
    <div className="space-y-2">
      {/* Step Header */}
      <div className="flex items-center gap-3">
        {getStepIcon(step, updates)}
        <span className={`text-sm ${textColor} flex-1`}>
          {stepLabels[step]}
        </span>

        {/* Info icon to expand/collapse details */}
        {hasDetails && (
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-1 hover:bg-neutral-100 rounded transition-colors"
            aria-label={isExpanded ? "Hide details" : "Show details"}
          >
            {isExpanded ? (
              <ChevronDown className="w-4 h-4 text-primary-600" />
            ) : (
              <Info className="w-4 h-4 text-neutral-400 hover:text-primary-600" />
            )}
          </button>
        )}
      </div>

      {/* Step Details (inline info messages) */}
      {detailUpdates.length > 0 && (
        <div className="ml-8 space-y-1">
          {detailUpdates.map((update, idx) => (
            <div key={idx} className="flex items-start gap-2 text-xs text-neutral-600">
              <ChevronRight className="w-3 h-3 mt-0.5 flex-shrink-0 text-neutral-400" />
              <span>{update.message}</span>
            </div>
          ))}
        </div>
      )}

      {/* Expandable Details Panel */}
      {isExpanded && hasDetails && latestUpdate.details && (
        <div className="ml-8 mt-2 p-3 bg-neutral-50 border border-neutral-200 rounded-lg">
          <div className="space-y-2">
            <h4 className="text-xs font-semibold text-neutral-700 mb-2">Stage Details</h4>

            {/* Context Gathering Details */}
            {step === 'context_gathering' && (
              <div className="space-y-1.5 text-xs">
                {/* Business Domain */}
                {latestUpdate.details.business_domain && (
                  <div className="mb-3">
                    <div className="flex justify-between mb-1">
                      <span className="text-neutral-600 font-semibold">Business Domain:</span>
                      <span className="font-medium text-neutral-900">{latestUpdate.details.business_domain}</span>
                    </div>
                    {latestUpdate.details.business_domain_description && (
                      <div className="text-neutral-600 text-xs bg-white p-2 rounded border border-neutral-200">
                        {latestUpdate.details.business_domain_description}
                      </div>
                    )}
                  </div>
                )}

                {/* Business Context */}
                {latestUpdate.details.business_context && (
                  <div className="mb-3">
                    <span className="text-neutral-600 font-semibold block mb-1">Business Context:</span>
                    <div className="text-neutral-700 text-xs bg-white p-2 rounded border border-neutral-200 max-h-32 overflow-y-auto whitespace-pre-wrap">
                      {latestUpdate.details.business_context}
                    </div>
                  </div>
                )}

                {/* Company Info */}
                {latestUpdate.details.company_name && (
                  <div className="flex justify-between">
                    <span className="text-neutral-600">Company:</span>
                    <span className="font-medium text-neutral-900">{latestUpdate.details.company_name}</span>
                  </div>
                )}
                {latestUpdate.details.company_industry && (
                  <div className="flex justify-between">
                    <span className="text-neutral-600">Industry:</span>
                    <span className="font-medium text-neutral-900">{latestUpdate.details.company_industry}</span>
                  </div>
                )}

                {/* Data Counts */}
                {latestUpdate.details.master_data_count !== undefined && (
                  <div className="flex justify-between">
                    <span className="text-neutral-600">Master Data Entities:</span>
                    <span className="font-medium text-neutral-900">{latestUpdate.details.master_data_count}</span>
                  </div>
                )}
                {latestUpdate.details.transactional_data_count !== undefined && (
                  <div className="flex justify-between">
                    <span className="text-neutral-600">Transactional Data:</span>
                    <span className="font-medium text-neutral-900">{latestUpdate.details.transactional_data_count}</span>
                  </div>
                )}
                {latestUpdate.details.systems_count !== undefined && (
                  <div className="flex justify-between">
                    <span className="text-neutral-600">Systems:</span>
                    <span className="font-medium text-neutral-900">{latestUpdate.details.systems_count}</span>
                  </div>
                )}
                {latestUpdate.details.executive_needs_count !== undefined && (
                  <div className="flex justify-between">
                    <span className="text-neutral-600">Executive Needs:</span>
                    <span className="font-medium text-neutral-900">{latestUpdate.details.executive_needs_count}</span>
                  </div>
                )}

                {/* Executive Needs List */}
                {latestUpdate.details.executive_needs_list && latestUpdate.details.executive_needs_list.length > 0 && (
                  <div className="mt-3">
                    <span className="text-neutral-600 font-semibold block mb-2">Executive Questions:</span>
                    <div className="bg-white p-3 rounded border border-neutral-200 max-h-48 overflow-y-auto space-y-2">
                      {latestUpdate.details.executive_needs_list.map((need: any, i: number) => (
                        <div key={i} className="flex items-start gap-2">
                          <span className="text-xs font-medium text-primary-600 flex-shrink-0 mt-0.5">
                            [{need.stakeholder || 'Executive'}]
                          </span>
                          <span className="text-xs text-neutral-700">{need.question}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Prompt Building Details */}
            {step === 'prompt_building' && (
              <div className="space-y-1.5 text-xs">
                {latestUpdate.details.prompt_length !== undefined && (
                  <div className="flex justify-between mb-2">
                    <span className="text-neutral-600">Prompt Length:</span>
                    <span className="font-medium text-neutral-900">{latestUpdate.details.prompt_length.toLocaleString()} chars</span>
                  </div>
                )}
                {latestUpdate.details.full_prompt && (
                  <div className="mt-2">
                    <span className="text-neutral-600 block mb-1 font-semibold">Full Prompt:</span>
                    <div className="bg-white p-3 rounded border border-neutral-200 text-neutral-700 font-mono text-xs max-h-96 overflow-y-auto whitespace-pre-wrap">
                      {latestUpdate.details.full_prompt}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Prompt Optimization Details */}
            {step === 'prompt_optimization' && (
              <div className="space-y-3 text-xs">
                {/* Optimization Summary */}
                {latestUpdate.details.reduction_percentage !== undefined && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle2 className="w-4 h-4 text-green-600" />
                      <span className="font-semibold text-green-900">Optimization Complete</span>
                    </div>
                    <div className="space-y-1.5">
                      <div className="flex justify-between">
                        <span className="text-neutral-600">Original Length:</span>
                        <span className="font-medium text-neutral-900">{latestUpdate.details.original_length?.toLocaleString()} chars</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-neutral-600">Optimized Length:</span>
                        <span className="font-medium text-neutral-900">{latestUpdate.details.optimized_length?.toLocaleString()} chars</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-neutral-600">Reduction:</span>
                        <span className="font-medium text-green-700">{latestUpdate.details.reduction_percentage.toFixed(1)}%</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Improvements Made */}
                {latestUpdate.details.improvements && latestUpdate.details.improvements.length > 0 && (
                  <div>
                    <span className="text-neutral-600 font-semibold block mb-2">Improvements Made:</span>
                    <ul className="bg-white p-3 rounded border border-neutral-200 space-y-1.5">
                      {latestUpdate.details.improvements.map((improvement: string, i: number) => (
                        <li key={i} className="flex items-start gap-2">
                          <CheckCircle2 className="w-3 h-3 text-green-600 flex-shrink-0 mt-0.5" />
                          <span className="text-neutral-700">{improvement}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Redundancies Removed */}
                {latestUpdate.details.removed_redundancies && latestUpdate.details.removed_redundancies.length > 0 && (
                  <div>
                    <span className="text-neutral-600 font-semibold block mb-2">Redundancies Removed:</span>
                    <ul className="bg-white p-3 rounded border border-neutral-200 space-y-1.5">
                      {latestUpdate.details.removed_redundancies.map((redundancy: string, i: number) => (
                        <li key={i} className="flex items-start gap-2">
                          <X className="w-3 h-3 text-neutral-500 flex-shrink-0 mt-0.5" />
                          <span className="text-neutral-600">{redundancy}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Optimized Prompt */}
                {latestUpdate.details.optimized_prompt && (
                  <div className="mt-2">
                    <span className="text-neutral-600 block mb-1 font-semibold">Optimized Prompt:</span>
                    <div className="bg-white p-3 rounded border border-neutral-200 text-neutral-700 font-mono text-xs max-h-96 overflow-y-auto whitespace-pre-wrap">
                      {latestUpdate.details.optimized_prompt}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* AI Generation Details */}
            {step === 'ai_generation' && (
              <div className="space-y-1.5 text-xs">
                {latestUpdate.details.provider && (
                  <div className="flex justify-between">
                    <span className="text-neutral-600">Provider:</span>
                    <span className="font-medium text-neutral-900">{latestUpdate.details.provider}</span>
                  </div>
                )}
                {latestUpdate.details.model && (
                  <div className="flex justify-between">
                    <span className="text-neutral-600">Model:</span>
                    <span className="font-medium text-neutral-900">{latestUpdate.details.model}</span>
                  </div>
                )}
                {latestUpdate.details.tokens !== undefined && (
                  <div className="flex justify-between">
                    <span className="text-neutral-600">Tokens Used:</span>
                    <span className="font-medium text-neutral-900">{latestUpdate.details.tokens.toLocaleString()}</span>
                  </div>
                )}
                {latestUpdate.details.thinking_tokens !== undefined && (
                  <div className="flex justify-between">
                    <span className="text-neutral-600">Thinking Tokens:</span>
                    <span className="font-medium text-neutral-900">{latestUpdate.details.thinking_tokens.toLocaleString()}</span>
                  </div>
                )}
              </div>
            )}

            {/* Validation Details */}
            {step === 'validation' && (
              <div className="space-y-1.5 text-xs">
                {latestUpdate.details.quality_score !== undefined && (
                  <div className="flex justify-between">
                    <span className="text-neutral-600">Quality Score:</span>
                    <span className="font-medium text-neutral-900">{latestUpdate.details.quality_score}/100</span>
                  </div>
                )}
                {latestUpdate.details.nodes !== undefined && (
                  <div className="flex justify-between">
                    <span className="text-neutral-600">Node Types:</span>
                    <span className="font-medium text-neutral-900">{latestUpdate.details.nodes}</span>
                  </div>
                )}
                {latestUpdate.details.relationships !== undefined && (
                  <div className="flex justify-between">
                    <span className="text-neutral-600">Relationship Types:</span>
                    <span className="font-medium text-neutral-900">{latestUpdate.details.relationships}</span>
                  </div>
                )}
                {latestUpdate.details.validation_errors && latestUpdate.details.validation_errors.length > 0 && (
                  <div className="mt-2">
                    <span className="text-red-600 block mb-1">Validation Errors:</span>
                    <ul className="list-disc list-inside space-y-0.5 text-red-600">
                      {latestUpdate.details.validation_errors.map((error: string, i: number) => (
                        <li key={i}>{error}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {/* Persistence Details */}
            {step === 'persistence' && (
              <div className="space-y-1.5 text-xs">
                {latestUpdate.details.saved_to_neo4j !== undefined && (
                  <div className="flex justify-between">
                    <span className="text-neutral-600">Saved to Neo4j:</span>
                    <span className="font-medium text-green-700">
                      {latestUpdate.details.saved_to_neo4j ? 'Yes' : 'No'}
                    </span>
                  </div>
                )}
                {latestUpdate.details.cypher_ddl_length !== undefined && (
                  <div className="flex justify-between">
                    <span className="text-neutral-600">Cypher DDL Length:</span>
                    <span className="font-medium text-neutral-900">{latestUpdate.details.cypher_ddl_length.toLocaleString()} chars</span>
                  </div>
                )}
              </div>
            )}

            {/* Generic details for any other fields */}
            {Object.entries(latestUpdate.details).map(([key, value]) => {
              // Skip fields we've already displayed above
              const displayedFields = [
                'master_data_count', 'transactional_data_count', 'systems_count',
                'executive_needs_count', 'executive_needs_list', 'business_domain',
                'business_domain_description', 'business_context', 'company_name',
                'company_industry', 'prompt_length', 'full_prompt', 'provider',
                'model', 'tokens', 'thinking_tokens', 'quality_score', 'nodes',
                'relationships', 'validation_errors', 'saved_to_neo4j', 'cypher_ddl_length',
                'original_length', 'optimized_length', 'reduction_percentage',
                'improvements', 'removed_redundancies', 'optimized_prompt'
              ];

              if (displayedFields.includes(key)) return null;

              return (
                <div key={key} className="flex justify-between text-xs">
                  <span className="text-neutral-600">{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}:</span>
                  <span className="font-medium text-neutral-900">
                    {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default function ProgressDisplay({ updates, currentProgress }: ProgressDisplayProps) {
  return (
    <div className="space-y-6">
      {/* Progress Bar */}
      <div className="space-y-2">
        <div className="flex items-center justify-between text-sm">
          <span className="text-neutral-700 font-medium">Progress</span>
          <span className="text-neutral-600">{currentProgress}%</span>
        </div>
        <div className="w-full h-2 bg-neutral-200 rounded-full overflow-hidden">
          <div
            className="h-full bg-primary-600 transition-all duration-500 ease-out"
            style={{ width: `${currentProgress}%` }}
          />
        </div>
      </div>

      {/* Steps */}
      <div className="space-y-4">
        {stepOrder.map((step) => (
          <StepItem key={step} step={step} updates={updates} />
        ))}
      </div>

      {/* Latest Message (if active) */}
      {updates.length > 0 && (
        (() => {
          const latestUpdate = updates[updates.length - 1];
          if (latestUpdate.status === 'started' || latestUpdate.status === 'in_progress') {
            return (
              <div className="mt-4 p-3 bg-primary-50 border border-primary-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <Loader2 className="w-4 h-4 text-primary-600 animate-spin mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-primary-700">{latestUpdate.message}</p>
                </div>
              </div>
            );
          }
          return null;
        })()
      )}

      {/* Error Display */}
      {updates.some((u) => u.status === 'failed' || u.status === 'error') && (
        <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-red-700">Generation Failed</p>
              <p className="text-xs text-red-600 mt-1">
                {updates.find((u) => u.status === 'failed' || u.status === 'error')?.message}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Success Message */}
      {updates.some((u) => u.step === 'persistence' && u.status === 'completed') && (
        <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-start gap-2">
            <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-green-700">Schema Generation Complete!</p>
              {(() => {
                const completionUpdate = updates.find((u) => u.step === 'persistence' && u.status === 'completed');
                if (completionUpdate?.details) {
                  return (
                    <p className="text-xs text-green-600 mt-1">
                      Generated {completionUpdate.details.nodes} nodes and {completionUpdate.details.relationships} relationships
                      {completionUpdate.details.quality_score && ` (Quality: ${completionUpdate.details.quality_score}/100)`}
                    </p>
                  );
                }
                return null;
              })()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
